// Журнал нарушений участника события. Записи приходят с бэкенда как есть
// (см. Api::ViolationsController) — здесь только разбор details и форматирование.
//
// Записи неизменяемые: правка релегации и ее снятие приходят отдельными строками,
// поэтому у одного нарушения в журнале может быть несколько записей подряд.

import { formatPenalty } from './relegation';
import { formatWeight, formatWeightDate } from './weightControl';
import { localeFor } from './timeFormat';

export const VIOLATION_KINDS = [
  'relegation',
  'weight_drop',
  'equipment_change',
  'power_outlier',
  'missing_heart_rate',
  'sticky_watts'
];

// Типы, которые ставит автоматика. Это повод посмотреть заезд, а не приговор —
// решение организатор оформляет релегацией
export const AUTOMATIC_VIOLATION_KINDS = [
  'weight_drop',
  'equipment_change',
  'power_outlier',
  'missing_heart_rate',
  'sticky_watts'
];

export const isAutomaticViolation = (violation) =>
  AUTOMATIC_VIOLATION_KINDS.includes(violation?.kind);

// Длительность интервала кривой мощности: 300 -> «5 мин», 45 -> «45 с»
const formatDuration = (seconds, t) => {
  const value = Number(seconds);
  if (!Number.isFinite(value) || value <= 0) return '—';

  if (value < 60) return `${Math.round(value)} ${t('violations.unit_seconds')}`;

  const minutes = Math.round(value / 60);
  return `${minutes} ${t('violations.unit_minutes')}`;
};

const formatPercent = (ratio) => {
  const value = Number(ratio);
  if (!Number.isFinite(value)) return '—';

  return `${Math.round((value - 1) * 100)}%`;
};

// Дата и время фиксации: у веса это дата заезда, у релегации — момент выдачи
export const formatViolationDate = (value, language) => {
  if (!value) return '';

  const time = Date.parse(value);
  if (Number.isNaN(time)) return '';

  return new Date(time).toLocaleString(localeFor(language), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Суть нарушения одной строкой: штраф или величина снижения веса
export const formatViolationSummary = (violation, t) => {
  const details = violation.details || {};

  if (violation.kind === 'relegation') {
    return t('violations.relegation_summary')
      .replace('{penalty}', formatPenalty(details.penalty_seconds));
  }

  if (violation.kind === 'weight_drop') {
    return t('violations.weight_summary')
      .replace('{drop}', formatWeight(details.drop))
      .replace('{weight}', formatWeight(details.weight));
  }

  if (violation.kind === 'equipment_change') {
    return t('violations.equipment_summary')
      .replace('{previous}', details.previous || '—')
      .replace('{current}', details.current || '—');
  }

  if (violation.kind === 'power_outlier') {
    return t('violations.power_summary')
      .replace('{watts}', details.watts ?? '—')
      .replace('{duration}', formatDuration(details.duration, t))
      .replace('{percent}', formatPercent(details.ratio));
  }

  if (violation.kind === 'missing_heart_rate') {
    return t('violations.heart_rate_summary');
  }

  if (violation.kind === 'sticky_watts') {
    return t('violations.sticky_summary').replace('{cv}', details.cv ?? '—');
  }

  return violation.kind;
};

// Расшифровка: причина релегации, прежний штраф при правке, замер, с которым
// сравнивался вес. Пустые строки отбрасываем — в карточке лишних пунктов не нужно
export const formatViolationDetails = (violation, t, language) => {
  const details = violation.details || {};
  const lines = [];

  if (violation.kind === 'relegation') {
    if (details.reason) {
      lines.push(t('violations.relegation_reason').replace('{reason}', details.reason));
    }
    if (details.previous_penalty_seconds != null) {
      lines.push(
        t('violations.relegation_previous')
          .replace('{penalty}', formatPenalty(details.previous_penalty_seconds))
      );
    }
  }

  if (violation.kind === 'weight_drop') {
    if (details.reference_weight != null) {
      lines.push(
        t('violations.weight_reference')
          .replace('{weight}', formatWeight(details.reference_weight))
          .replace('{date}', formatWeightDate(details.reference_date, language))
          .replace('{name}', details.reference_event_name || '—')
      );
    }
    const rideName = details.event_name || details.route_name;
    if (rideName) {
      lines.push(t('violations.weight_ride').replace('{name}', rideName));
    }
  }

  if (violation.kind === 'equipment_change' && details.previous_date) {
    lines.push(
      t('violations.equipment_previous')
        .replace('{name}', details.previous_route_name || '—')
        .replace('{date}', formatWeightDate(details.previous_date, language))
    );
  }

  if (violation.kind === 'power_outlier') {
    if (details.previous_best != null) {
      lines.push(
        t('violations.power_previous')
          .replace('{watts}', details.previous_best)
          .replace('{rides}', details.history_rides ?? '—')
      );
    }
    if (details.weight != null) {
      lines.push(t('violations.power_weight').replace('{weight}', formatWeight(details.weight)));
    }
  }

  if (violation.kind === 'missing_heart_rate' && details.history_rides != null) {
    lines.push(t('violations.heart_rate_history').replace('{rides}', details.history_rides));
  }

  if (violation.kind === 'sticky_watts') {
    lines.push(
      t('violations.sticky_details')
        .replace('{avg}', details.avg_power ?? '—')
        .replace('{duration}', formatDuration(details.duration, t))
    );
  }

  // У автоматических флагов маршрут кладется в details: этап уже показан рядом,
  // но у заезда вне текущей серии своего route у записи может и не быть
  if (isAutomaticViolation(violation) && !violation.route && details.route_name) {
    lines.push(t('violations.weight_ride').replace('{name}', details.route_name));
  }

  return lines;
};

// Этап нарушения: номер как в протоколе плюс название маршрута.
// У контроля веса этапа нет — снижение фиксируется по заезду, а не по этапу тура
export const formatViolationStage = (violation, stageNumbers) => {
  if (!violation.route) return null;

  const number = stageNumbers[violation.route.id];
  return number ? `${number}. ${violation.route.name}` : violation.route.name;
};

export const getViolationRiderName = (rider, language) => {
  if (!rider) return '';

  return language !== 'ru' && rider.first_name_eng && rider.last_name_eng
    ? `${rider.first_name_eng} ${rider.last_name_eng}`
    : `${rider.first_name} ${rider.last_name}`;
};
